import React, {useState} from 'react';
import moment from 'moment';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import uniq from 'lodash/uniqBy';

import Box from '@mui/material/Box';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import AddressField from '../common/AddressField';
import CustomInput from '../common/CustomInput';
import CustomInputLabel from '../common/CustomInputLabel';
import CustomSelect from '../common/CustomSelect';
import CustomTextField from '../common/CustomTextField';
import ChoiceField from '../common/ChoiceField';
import DayPicker from '../common/DayPicker';
import DateField from '../common/DateField';
import TimeField from '../common/TimeField';
import NumberField from '../common/CustomNumberField';

import useTimezone from '../../hooks/useTimezone';
import {useGetSchedulesQuery} from '../../redux/services/scheduleApi';
import {
  useGetProviderAvailabilitiesQuery
} from '../../redux/services/providerAvailabilityApi';
import {getDays} from '../../helpers/scheduling';
import config from '../../config/config';
import TimeSlot from './TimeSlot';

const getDayQueryParams = ({providerId, serviceCategory, date}) => {
  let query = {
    service_category: serviceCategory,
    start_time: moment(date).startOf('day').toISOString(),
    end_time: moment(date).endOf('day').toISOString(),
  };
  if (providerId) query.provider_id = providerId;
  return query;
};

const getWeekQueryParams = ({providerId, serviceCategory, date}) => {
  let query = {
    service_category: serviceCategory,
    start_time: moment(date).startOf('week').toISOString(),
    end_time: moment(date).endOf('week').toISOString(),
  };
  if (providerId) query.provider_id = providerId;
  return query;
};

const availabilityMatchesTime = ({time}) => (availability) => {
  if (!time) return false;
  const momentTime = moment(time);
  const startMinute = momentTime.hour() * 60 + momentTime.minute();
  const match = (availability.day_availabilities || []).find(da => 
    da.day === momentTime.format('ddd').toUpperCase()
    && da.start_minute <= startMinute
    && da.end_minute >= startMinute
  );
  return !!match;
};
  
const LocationChoiceField = props => {
  const childProps = {
    onChange: props.onChange,
    color: 'purple',
    defaultValue: props.defaultValue,
    label: 'Location Type',
    required: true,
    options: [
      {label: '🏋️ Facility', value: 'PARTNER'},
      {label: '🏠 In-Home', value: 'HOME'},
      {label: '🚗 Mobile', value: 'MOBILE'},
      {label: '💻 Virtual', value: 'VIRTUAL'},
    ].filter(opt => (props?.locationTypes || []).includes(opt.value)),
    disabled: props.disabled || false,
  };
  return (
    <Box style={{minWidth: '400px'}}>
      <ChoiceField {...childProps} />
    </Box>
  );
};

const LocationDetailsForm = props => {
  const {locationType, providerAvailability, sessionConfig} = props;
  if (locationType === 'VIRTUAL') {
    return (
      <CustomTextField
        label='Meeting Link'
        required={true}
        color='purple'
        minWidth='350px'
        defaultValue={providerAvailability?.session_meeting_url}
        onChange={(e, val) => props.onChange({session_meeting_url: e?.target?.value})}
      />
    );
  }
  if (locationType === 'PARTNER') {
    return (
      <CustomSelect
        fullWidth
        label='Location'
        required={true}
        color='purple'
        options={(sessionConfig?.locations || []).map(location => ({
          label: location?.name,
          value: location.id,
        }))}
        minWidth='350px'
        onChange={(e, val) => {
          props.onChange({location_id: e.target.value});
        }}
      />
    );
  }
  if (locationType === 'HOME') {
    // return <CustomInput label='Location'>🏠 Patient's Home</CustomInput>;
    return null;
  }
  if (locationType === 'MOBILE' && !props.isPatient) {
    return (
      <Box style={{minWidth: '400px', width: '100%'}}>
        <AddressField
          label='Address'
          required={true}
          color='purple'
          minWidth='400px'
          fullWidth
          editable={false}
          onChange={(address, val) => {
            props.onChange({address});
          }}
        />
      </Box>
    );
  }
};

const parseLocationDetails = (details, locationType) => {
  if (locationType === 'VIRTUAL') {
    return {session_meeting_url: details};
  }
  if (locationType === 'PARTNER') {
    return {location_id: details};
  }
  if (locationType === 'HOME') {
    return {};
  }
  if (locationType === 'MOBILE') {
    console.log('PARSING MOBILE DETAILS', details);
    return {address: details};
  }
  return {};
};

const handleFieldChange = ({
  formData, 
  setFormData, 
  providerAvailability,
  onChange
}) => fieldName => (e, val) => {
  let newData;
  if (fieldName === 'start_time') {
    // when start time changes, reset location details
    newData = pick(formData, 'override_schedule');
  } else if (fieldName === 'location_type') {
    newData = omit(formData, 'address', 'location_id', 'session_meeting_url');
    if (val === 'VIRTUAL') {
      newData.location_details = {
        session_meeting_url: providerAvailability?.session_meeting_url || ''
      };
    } else {
      newData.location_details = {};
    }
  } else {
    newData = omit(formData, 'override_schedule');
  }
  if (fieldName === 'start_time' && val) {
    newData.start_time = val.start_time;
    newData.provider_availability_id = val.provider_availability_id;
  } else if (fieldName === 'override_schedule' && val) {
    newData.override_schedule = true;
  } else if (fieldName === 'location_details') {
    newData = {
      ...newData, 
      ...parseLocationDetails(
        e?.address 
        ? e.address
        : e.location_id ? e.location_id
        : val, 
        formData.location_type
      )
    };
  } else {
    newData[fieldName] = val;
  }
  setFormData(newData);
  console.log('field change', fieldName, val, newData);
  onChange(e, newData);
};

const SchedulerInput = ({
  providerId,
  defaultValue = null,
  serviceCategory = 'PHYSICAL_THERAPY',
  color = 'primary',
  allowOverrideSchedule = true,
  durationDefault = 60,
  onChange,
  isPatient = false,
  disableLocationChoices = false,
}) => {
  const [formData, setFormData] = useState(
    defaultValue 
    ? {
      start_time: defaultValue.start_time, 
      duration: defaultValue.duration,
      location_type: defaultValue.location_type,
      location_details: defaultValue.location_details,
    }
    : {}
  );
  const [date, setDate] = useState(
    defaultValue?.start_time
    ? moment(defaultValue.start_time).toDate()
    : new Date()
  );
  const timezone = useTimezone();
  const override = !!formData?.override_schedule || false;
  const time = formData.start_time || null;
  const timeSlotsQuery = useGetSchedulesQuery(getDayQueryParams({
    providerId,
    serviceCategory,
    date
  }));
  const daysQuery = useGetSchedulesQuery(getWeekQueryParams({
    providerId,
    serviceCategory,
    date,
  }));
  const availabilitiesQuery = useGetProviderAvailabilitiesQuery({
    provider_id: providerId
  });
  const availabilities = availabilitiesQuery?.data?.data || [];
  // map to the dates that are available
  const availableDates = (daysQuery?.data?.data || []).map(
    slot => moment(slot.start_time).format('YYYY-MM-DD')
  );
  const availableDays = availableDates.map(
    dateStr => moment(dateStr).format('ddd').toUpperCase()
  );
  const disabledDays = getDays().filter(d => !availableDays.includes(d));
  const timeSlots = uniq(timeSlotsQuery?.data?.data || [], s => s.start_time);
  const availability = availabilities.find(availabilityMatchesTime({time}));
  const sessionConfig = (
    !availability || override
    ? null
    : availability.configs.find(conf => 
      conf.session_type.service_category === serviceCategory
    )
  );
  const locationTypes = (
    override
    ? ['PARTNER', 'HOME', 'MOBILE', 'VIRTUAL']
    : (sessionConfig?.location_types || [])
  );
  const facilities = override ? [] : (sessionConfig?.locations || []);
  const onFieldChange = handleFieldChange({
    formData, 
    setFormData, 
    onChange,
    providerAvailability: availability,
  });
  const noMatchingTimeSlot = (
    time 
    && !timeSlots.find(slot => slot.start_time === time)
  );
  return (
    <Box>
      {!!allowOverrideSchedule && <Box style={{paddingTop: '1em', paddingBottom: '1em'}}>
        <ChoiceField 
          defaultValue={false}
          color={color}
          options={[
            {value: false, label: 'Show Schedule'},
            {value: true, label: 'Override Schedule'},
          ]}
          onChange={(e, val) => onFieldChange('override_schedule')(e, val)}
        />
      </Box>}
      {!override && 
        <Stack>
          <Stack 
            direction='row' 
          >
            <Stack spacing={1} flex={1}>
              <CustomInputLabel required={true}>Day</CustomInputLabel>
              <Stack alignItems='center'>
                <DayPicker
                  color={color}
                  date={date}
                  allowPastDays={false}
                  onChange={(e, val) => {
                    setDate(val);
                    onFieldChange('start_time')(e, null);
                  }}
                  disabledDays={disabledDays}
                />
              </Stack>
              <Stack 
                direction='row' 
                spacing={2} 
                justifyContent='center'
                alignItems='center'
              >
                <IconButton 
                  size='small' 
                  color={color}
                  onClick={e => {
                    setDate(moment(date).subtract(1, 'week').toDate());
                    onFieldChange('start_time')(e, null);
                  }}
                >
                  <ChevronLeft /> 
                </IconButton>
                <Typography variant='body2'>
                  {moment(date).startOf('week').format('MMM D')} 
                  {' '}-{' '}
                  {moment(date).endOf('week').format('MMM D')} 
                </Typography>
                <IconButton 
                  size='small' 
                  color={color}
                  onClick={e => {
                    setDate(moment(date).add(1, 'week').toDate());
                    onFieldChange('start_time')(e, null);
                  }}
                >
                  <ChevronRight /> 
                </IconButton>
              </Stack>
            </Stack>
            <Stack spacing={1} pl={2} flex={1}>
              <CustomInputLabel required={true}>Time</CustomInputLabel>
              <Stack direction='row' gap={1} pt={1} flexWrap='wrap'>
                  {(timeSlots || []).map((slot, i) => 
                    <TimeSlot
                      key={`slot-${i}`}
                      timeSlot={slot}
                      selected={formData.start_time === slot.start_time}
                      onClick={(e, slot) => {
                        onFieldChange('start_time')(e, slot);
                      }}
                      color={color}
                    />
                  )
                  }
                  {
                    noMatchingTimeSlot && !!formData.start_time && (
                      <TimeSlot 
                        key={`default-time-slot`}
                        timeSlot={{start_time: formData.start_time}}
                        selected={true}
                        color={color}
                      />
                    )
                  }
              </Stack>
            </Stack>
          </Stack>
        </Stack> 
      }
      {override && 
        <Grid container gap={1}>
          <Grid item xs={12} md={6}>
            <Box style={{paddingTop: '1em'}}>
              <DateField
                label='Date'
                defaultValue={date}
                color={color}
                onChange={(e, val) => setDate(val)}
              />
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <TimeField
              label='Time'
              defaultValue={formData.start_time}
              style={{width: '120px'}}
              maxWidth='120px'
              color={color}
              onChange={(e, val) => {
                // console.log('TIME_CHANGE val,moment,date', val, moment(val), date);
                const dateStr = moment(date).format('YYYY-MM-DD');
                const timeStr = val; // h:mm A
                const dtStr = `${dateStr} ${timeStr}`;
                const datetime = moment.tz(dtStr, 'YYYY-MM-DD h:mm A', timezone);
                // console.log('NEW_DATETIME', datetime.tz(timezone).format('YYYY-MM-DD h:mm A'));
                onFieldChange('start_time')(e, {start_time: datetime.toISOString()});
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <NumberField
              min={1}
              max={1000}
              label='Duration (minutes)'
              color={color}
              maxWidth='150px'
              defaultValue={formData.duration || durationDefault}
              onChange={(e, val) => onFieldChange('duration')(e, e.target.value)}
            />
          </Grid>
        </Grid>
      }
      {(disableLocationChoices 
        || ((override || !!availability) && locationTypes.length > 0)) &&
        <Stack direction='row' spacing={2} pt={2}>
          <LocationChoiceField
            key={formData.start_time}
            locationTypes={locationTypes}
            defaultValue={formData.location_type}
            disabled={disableLocationChoices}
            onChange={(e, val) => {
              onFieldChange('location_type')(e, val);
            }}
          />
          {!!formData.location_type &&
            <LocationDetailsForm
              key={formData.start_time}
              locationType={formData.location_type} 
              defaultValue={formData.location_details}
              providerAvailability={availability}
              sessionConfig={sessionConfig}
              isPatient={isPatient}
              onChange={(e, val) => {
                onFieldChange('location_details')(e, val);
              }}
            />
          }
        </Stack>
      }
    </Box>
  );
};

export default SchedulerInput;